import * as React from 'react'

import { cn } from './cn'
import CircularProgress from './CircularProgress'

export type ButtonVariant = 'text' | 'outlined' | 'contained'
export type ButtonColor =
  | 'primary'
  | 'secondary'
  | 'error'
  | 'warning'
  | 'info'
  | 'success'
  | 'inherit'
export type ButtonSize = 'small' | 'medium' | 'large'

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  variant?: ButtonVariant
  color?: ButtonColor
  size?: ButtonSize
  fullWidth?: boolean
  startIcon?: React.ReactNode
  endIcon?: React.ReactNode
  /** Renders an anchor styled as a button. */
  href?: string
  target?: string
  rel?: string
  download?: string
  /** Shows a spinner and disables the button (old LoadingButton). */
  loading?: boolean
  component?: React.ElementType
  to?: string
}

const colorVariants: Record<ButtonVariant, Record<ButtonColor, string>> = {
  contained: {
    primary:
      'bg-primary text-primary-contrast shadow-paper hover:bg-primary-dark',
    secondary:
      'bg-primary text-primary-contrast shadow-paper hover:bg-primary-dark',
    error: 'bg-error text-white shadow-paper hover:bg-error-dark',
    warning: 'bg-warning text-white shadow-paper hover:bg-warning-dark',
    info: 'bg-info text-white shadow-paper hover:bg-info-dark',
    success: 'bg-success text-white shadow-paper hover:bg-success-dark',
    inherit:
      'bg-surface text-text-primary border border-divider shadow-paper hover:bg-divider',
  },
  outlined: {
    primary:
      'border border-primary/35 text-primary hover:border-primary/60 hover:bg-primary/[0.06]',
    secondary:
      'border border-primary/35 text-primary hover:border-primary/60 hover:bg-primary/[0.06]',
    error:
      'border border-error/35 text-error hover:border-error/60 hover:bg-error/[0.06]',
    warning:
      'border border-warning/40 text-warning-dark dark:text-warning hover:border-warning/70 hover:bg-warning/[0.06]',
    info: 'border border-info/35 text-info hover:border-info/60 hover:bg-info/[0.06]',
    success:
      'border border-success/35 text-success hover:border-success/60 hover:bg-success/[0.06]',
    inherit:
      'border border-divider-strong text-inherit hover:bg-black/[0.04] dark:hover:bg-white/[0.06]',
  },
  text: {
    primary: 'text-primary hover:bg-primary/[0.08]',
    secondary: 'text-primary hover:bg-primary/[0.08]',
    error: 'text-error hover:bg-error/[0.08]',
    warning: 'text-warning-dark dark:text-warning hover:bg-warning/[0.08]',
    info: 'text-info hover:bg-info/[0.08]',
    success: 'text-success hover:bg-success/[0.08]',
    inherit: 'text-inherit hover:bg-black/[0.04] dark:hover:bg-white/[0.06]',
  },
}

const sizeClasses: Record<ButtonSize, string> = {
  small: 'px-2.5 py-1 text-xs rounded-lg min-w-0 gap-1.5',
  medium: 'px-4 py-2 text-sm rounded-field gap-2',
  large: 'px-5 py-2.5 text-base rounded-field gap-2',
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'text',
      color = 'primary',
      size = 'medium',
      fullWidth,
      startIcon,
      endIcon,
      loading,
      disabled,
      className,
      children,
      href,
      component,
      ...props
    },
    ref,
  ) => {
    const classes = cn(
      'inline-flex items-center justify-center font-medium leading-none',
      'transition-all duration-150 cursor-pointer select-none active:scale-[0.98]',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-paper',
      'disabled:pointer-events-none disabled:opacity-40 disabled:shadow-none',
      sizeClasses[size],
      colorVariants[variant][color],
      fullWidth && 'w-full',
      (disabled || loading) && 'pointer-events-none opacity-40 shadow-none',
      className,
    )

    const content = (
      <>
        {loading ? (
          <CircularProgress size={16} color="inherit" />
        ) : (
          startIcon && <span className="inline-flex shrink-0">{startIcon}</span>
        )}
        {children}
        {endIcon && <span className="inline-flex shrink-0">{endIcon}</span>}
      </>
    )

    const Component: React.ElementType = component || (href ? 'a' : 'button')

    return (
      <Component
        ref={ref}
        href={href}
        disabled={Component === 'button' ? disabled || loading : undefined}
        aria-disabled={disabled || loading || undefined}
        type={
          Component === 'button' && !(props as { type?: string }).type
            ? 'button'
            : undefined
        }
        className={classes}
        {...props}
      >
        {content}
      </Component>
    )
  },
)
Button.displayName = 'Button'

export default Button

/** Compatibility alias for the old lab LoadingButton. */
export const LoadingButton = Button
